import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Check, X } from 'lucide-react';
import { ACTIONS } from '../../redux/actions';
import { toast } from 'react-toastify';
import { path } from '../../Utils/ScreenPath';
import LogOutfunction from '../../helper/LogOutfunction';
import login from "../../assets/login.png";


export default function ChangePasswordPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ChangePasswordData, ChangePasswordLoading } = useSelector(state => state.ChangePassword);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Handle API response
  useEffect(() => {
    if (ChangePasswordData) {
      if (ChangePasswordData.success) {
        toast.success(ChangePasswordData.message || 'Password changed successfully!');
        setSuccess(true);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Show logout message and automatically logout after 3 seconds
        setTimeout(() => {
          toast.info('You will be logged out automatically for security reasons...', {
            autoClose: 2000,
          });
          
          // Logout after showing the message
          setTimeout(() => {
            // Clear Redux state
            dispatch(ACTIONS.clearLogin());
            
            // Clear localStorage
            LogOutfunction();
            
            // Reload the page to clear all Redux state
            window.location.reload();
          }, 2000);
        }, 1000);
      } else {
        toast.error(ChangePasswordData.message || 'Failed to change password. Please try again.');
      }
    }
  }, [ChangePasswordData, dispatch, navigate]);

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors as user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else {
      const requirements = validatePassword(formData.newPassword);
      if (!Object.values(requirements).every(Boolean)) {
        newErrors.newPassword = 'Password does not meet requirements';
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Prepare API payload according to the required format
    const payload = JSON.stringify({
      oldpassword: formData.currentPassword,
      newpassword: formData.newPassword,
      confirmPassword: formData.confirmPassword
    });

    // Dispatch the change password action
    dispatch(ACTIONS.ChangePasswordAction(payload));
  };

  const requirements = validatePassword(formData.newPassword);

  return (
    <div className="flex justify-center p-6">
      <div className="w-full max-w-md">


        {/* Form */}
        <div className="space-y-6">
          <div className="bg-[#FFFFFF]   backdrop-blur-md rounded-xl p-6 shadow-xl">
            {/* Header */}
            <div className="mb-8">
              <div className="w-16 h-16 mb-4">
                <img src={login} alt="Desktop" />
              </div>
              <h1 className="text-[20px] font-bold text-[#1F1715] mb-2">Change Password</h1>
            </div>

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg backdrop-blur-sm animate-pulse">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-400 mr-3" />
                  <span className="text-[#1F1715]">Password changed successfully!</span>
                </div>
              </div>
            )}
            {/* Current Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1715] mb-2">
                Current Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-[#FFFFFF] border border-[#BEBEBE] rounded-lg text-[#1F1715] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gray focus:border-transparent transition-all duration-200"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 "
                >
                  {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="mt-2 text-sm text-red-400">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1715] mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-[#FFFFFF] border border-[#BEBEBE] rounded-lg text-[#1F1715] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gray focus:border-transparent transition-all duration-200"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 "
                >
                  {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-2 text-sm text-red-400">{errors.newPassword}</p>
              )}
            </div>

            {/* Password Requirements */}
            {formData.newPassword && (
              <div className="mb-6 p-4 bg-[#ffffff] rounded-lg border border-slate-600/50">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Password Requirements</h4>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  {[
                    { key: 'length', label: 'At least 8 characters' },
                    { key: 'uppercase', label: 'One uppercase letter' },
                    { key: 'lowercase', label: 'One lowercase letter' },
                    { key: 'number', label: 'One number' },
                    { key: 'special', label: 'One special character' }
                  ].map(req => (
                    <div key={req.key} className="flex items-center">
                      {requirements[req.key] ? (
                        <Check className="w-4 h-4 text-green-400 mr-2" />
                      ) : (
                        <X className="w-4 h-4 text-red-400 mr-2" />
                      )}
                      <span className={requirements[req.key] ? 'text-green-400' : 'text-slate-400'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1715] mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Lock className="w-5 h-5 text-slate-400" />
                </div>
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-[#FFFFFF] border border-[#BEBEBE] rounded-lg text-[#1F1715] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-gray focus:border-transparent transition-all duration-200"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 "
                >
                  {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Security Notice */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Security Notice:</strong> For your security, you will be automatically logged out after changing your password.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={ChangePasswordLoading}
              className="w-full border border-red-500 text-red-500 py-2 px-4  bg-[#EE3B310D] font-bold
              hover:bg-red-500 hover:text-white 
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-center 
              transition duration-200 ease-in-out  rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {ChangePasswordLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500 mr-2"></div>
                  Changing Password...
                </div>
              ) : (
                'Change Password'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}